import { getToken } from "@/services/authStorage";

import type { EventCatalog, EventName } from "@/lib/analytics/catalog";

/**
 * Resolved here rather than imported from apiClient.ts on purpose. apiClient
 * imports this module (its response interceptor emits `api.failed`), so taking
 * `apiBaseUrl` from it would close an import cycle — one that happens to work
 * today because the value is only read inside functions, but would break the
 * moment anything read it at module-init time. Three duplicated lines are the
 * cheaper side of that trade.
 */
const apiBaseUrl =
  typeof import.meta.env.VITE_API_URL === "string" &&
  import.meta.env.VITE_API_URL.length > 0
    ? import.meta.env.VITE_API_URL.replace(/\/$/, "")
    : "http://localhost:3001";

/** Flush once this many events are buffered. */
const FLUSH_AT_COUNT = 20;

/** Flush at least this often while the tab is active. */
const FLUSH_INTERVAL_MS = 10_000;

/** Server-side cap; matches MAX_EVENT_BATCH in product-event.service.ts. */
const MAX_BATCH = 100;

/**
 * Ceiling on the buffer while offline or unauthenticated. Old events are
 * dropped first: a bounded memory footprint matters more than a complete
 * history, and the newest events are the ones being asked about.
 */
const MAX_BUFFER = 500;

const SESSION_STORAGE_KEY = "fx.analytics.session";

interface QueuedEvent {
  name: EventName;
  occurredAt: string;
  sessionId: string;
  app: "portal";
  appVersion?: string;
  path?: string;
  props?: Record<string, unknown>;
}

/**
 * MODULE STATE IS SINGLE-WRITER BY CONSTRUCTION.
 *
 * These are mutable module-level globals, which in a threaded runtime would
 * need a lock. They do not here: this module only ever runs on the browser's
 * main thread, which is single-threaded and executes each task to completion.
 * `track()` and `flush()` are plain synchronous functions up to the point they
 * hand off to fetch, so no two of them can interleave mid-statement.
 *
 * The one genuinely concurrent-looking path is two overlapping flushes (the
 * 10s timer firing while an unload flush is in flight). That is safe because
 * each flush splices its batch out of `buffer` SYNCHRONOUSLY, before the first
 * await — so the second flush can never observe or resend the first one's
 * events. Preserve that ordering if this is ever refactored.
 *
 * A Web Worker or SharedWorker would break these assumptions; nothing here
 * runs in one today.
 */
let buffer: QueuedEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let enabled = true;
let authenticated = false;

/**
 * Per-tab session id. sessionStorage rather than a module variable so a reload
 * continues the same journey, and per-tab rather than localStorage so two tabs
 * are two journeys — which is what "reconstruct one person's path" needs.
 */
function sessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // Private mode or storage disabled — a per-call id still groups nothing,
    // but it keeps the event valid rather than dropping it.
    return crypto.randomUUID();
  }
}

/** Disables all emission for the rest of the page's life. */
export function disableAnalytics(): void {
  enabled = false;
  buffer = [];
}

/**
 * Marks the session authenticated so buffered pre-auth events can be sent.
 *
 * The ingest endpoint requires a token, so events emitted during login are
 * held rather than dropped, then flushed once auth resolves — they keep their
 * real `occurredAt`, so the pre-login steps are attributed retroactively.
 */
export function setAuthenticated(value: boolean): void {
  authenticated = value;
  if (value) void flush();
}

/**
 * Records an event. Never throws and never returns a rejected promise —
 * a broken analytics path must not be able to break invoicing.
 */
export function track<N extends EventName>(
  name: N,
  props: EventCatalog[N],
  options: { path?: string } = {},
): void {
  if (!enabled) return;
  try {
    buffer.push({
      name,
      occurredAt: new Date().toISOString(),
      sessionId: sessionId(),
      app: "portal",
      appVersion: readAppVersion(),
      path: options.path,
      props: props as Record<string, unknown>,
    });
    if (buffer.length > MAX_BUFFER) {
      buffer = buffer.slice(buffer.length - MAX_BUFFER);
    }
    if (buffer.length >= FLUSH_AT_COUNT) void flush();
  } catch {
    // Swallowed on purpose. See the never-throw contract above.
  }
}

let cachedVersion: string | undefined;
function readAppVersion(): string | undefined {
  if (cachedVersion !== undefined) return cachedVersion;
  try {
    const raw = (import.meta as { env?: Record<string, unknown> }).env?.VITE_APP_VERSION;
    cachedVersion = typeof raw === "string" && raw.length > 0 ? raw.slice(0, 32) : "";
  } catch {
    cachedVersion = "";
  }
  return cachedVersion === "" ? undefined : cachedVersion;
}

/**
 * Sends whatever is buffered.
 *
 * Deliberately NOT sent through `apiClient`: that instance carries the
 * response interceptor which emits `api.failed`, so a failing /events call
 * would emit an event describing its own failure and flush again — an
 * unbounded loop. It also owns the 401 refresh-and-redirect behaviour, which
 * telemetry has no business triggering.
 */
export async function flush(): Promise<void> {
  if (!enabled || !authenticated || buffer.length === 0) return;
  const token = getToken();
  if (!token) return;
  const batch = buffer.slice(0, MAX_BATCH);
  buffer = buffer.slice(batch.length);
  try {
    await fetch(`${apiBaseUrl}/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(batch),
      // Lets the request outlive the page, so this same function works both
      // on the timer and on unload.
      keepalive: true,
    });
  } catch {
    // Dropped rather than retried. A retry queue would risk unbounded growth
    // and duplicate rows for data that is, by design, lossy.
  }
}

/**
 * Flushes as the page goes away.
 *
 * NOT navigator.sendBeacon, despite that being the usual answer: sendBeacon
 * cannot set request headers, and this endpoint is authenticated with a Bearer
 * token. `fetch(..., { keepalive: true })` is the variant that both survives
 * unload and carries an Authorization header.
 */
function flushOnHide(): void {
  void flush();
}

/** Starts the periodic flush and the unload hook. Idempotent. */
export function startAnalytics(): void {
  if (timer !== null) return;
  try {
    timer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
    window.addEventListener("pagehide", flushOnHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushOnHide();
    });
  } catch {
    // A missing DOM API must not stop the app from booting.
  }
}
